import angular from 'angular'
import { ApiProduct } from '@vend/types'

import { VdFeatureManager } from '@vend/vend.js'
import { trackError } from '@vend/utilities'
import { $q } from '../../ng-compat/imports'
import { EntityStore } from './entity-store'
import { Cursor } from './database-types'

// A type to be used by the `byProductCode` IndexedDB index.
export interface DbProduct extends ApiProduct {
  product_codes_array: string[]
}

/**
 * Stores information on the retailer's products.
 */
export class ProductsStore extends EntityStore<'products'> {
  constructor(private readonly vdFeatureManager: VdFeatureManager) {
    'ngInject'
    super({
      entityStoreName: 'products',
    })
  }

  /**
   * Add or update the given entities in the IndexedDB store.
   *
   * @param  entities
   *         The array of entities to add/update
   *
   * @return A promise resolved with the array of entities that were successfully updated
   */
  protected updateEntities(entities: DbProduct[]) {
    entities.forEach(this.transformProduct)
    return super.updateEntities(entities)
  }

  /**
   * @param parentProduct {Product}
   *        The variant parent product (note: also a variant product).
   *
   * @return Collection of related variant products associated to the given parent product.
   */
  public getAllVariantProducts(parentProduct: ApiProduct) {
    return $q
      .resolve(this.dbStore.index('byVariantParentId').getAll(parentProduct.id))
      .then(function (variantProducts) {
        const availableVariantProducts = [...variantProducts]

        // Add the parent product to the collection (since it's also a variant product)
        availableVariantProducts.unshift(parentProduct)

        // Ignore disabled variant products and sort by button order
        return availableVariantProducts
          .filter(variantProduct => variantProduct.active)
          .sort((v1, v2) => v1.button_order - v2.button_order)
      })
  }

  /**
   * Get the variant count for a given product id, since all products are variants,
   * some just happen to have more than 1, we add a +1 so we can have a true variant count.
   * You can see the same being done in 'getAllVariantProducts' with 'unshift'
   *
   * @param productId The product ID to look up
   *
   * @return The variant count for a given product id
   */
  public getVariantCount(productId: string) {
    return $q.resolve(
      this.dbStore
        .index('byVariantParentId')
        .count(productId)
        .then(count => count + 1)
    )
  }

  /**
   *
   * Transforms product entities to produce a product_codes_array field to be used by the `byProductCode` IndexDB index.
   *
   * @param product that needs transforming.
   *
   * @return the modified product that include a `product_codes_array` field.
   */
  public transformProduct(product: DbProduct) {
    // Being defensive here by using the sku as a default.
    product.product_codes_array = [product.sku]

    if (product.product_codes && product.product_codes.length > 0) {
      product.product_codes_array = product.product_codes.map(v => v.code)
    }

    return product
  }

  /**
   * Removes any deleted products from the store. Note that although deleted products arent synced, they are
   * temporarily stored whilst handling sales that contain them.
   *
   * @returns {Promise} Resolved once all deleted products have been removed from the store.
   */
  public removeDeletedProducts() {
    return this.getDeletedProductIds()
      .then(productsToRemove => {
        if (Object.keys(productsToRemove).length) {
          return $q.resolve(this.dbStore.batch(productsToRemove))
        }
      })
      .catch(error => {
        trackError('[ProductsStore] Failed to clean up deleted products.', {
          data: { error },
        })
        return $q.resolve()
      })
  }

  public getAdditionalSyncParams() {
    if (this.vdFeatureManager.get('limit_product_images_in_webregister')) {
      return { include_images: false }
    }
  }

  /**
   * Gets the current collection of deleted products in the store (products whose deleted_at value is not null).
   *
   * @returns Key value (null) pairs of deleted products currently in the store.
   */
  private getDeletedProductIds() {
    const products: Record<string, null> = {}

    const iterator = (cursor: Cursor<'products'>) => {
      const product = cursor.value
      // Constructing an object of key values pairs (with null values) allows us to batch delete the
      // products from the store
      products[product.id] = null

      return true
    }

    return this.dbStore
      .index('byDeletedAt')
      .cursor({ iterator })
      .then(() => products)
  }
}

/**
 * Responsible for persisting storage of products from the /api/2.0/products API end point into IndexedDB.
 *
 * @module productsStore
 */
angular.module('webregister.factories').service('productsStore', ProductsStore)
